import React, { useRef, useState } from "react";
import classnames from "classnames";

import { getLongTitle, getShortTitle, MenuType } from "utils/menuTypeUtils";

import { InformationalModal } from "components/botanic/Modal";
import Radio from "components/botanic/Radio";
import SolidArrowIcon from "components/Icons/arrows/SolidArrowIcon";
import RadioGroup from "components/RadioGroup";

const MODAL_OPTIONS = Object.freeze([
  { label: getLongTitle(MenuType.Rec), value: MenuType.Rec },
  { label: `${getLongTitle(MenuType.Med)}*`, value: MenuType.Med },
]);

export const MenuTypeButton: React.FC<{
  color?: string;
  currentType: MenuType;
  onMenuTypeChange: (value: MenuType) => void;
}> = ({ currentType, onMenuTypeChange, color = "white" }) => {
  const [opened, setOpened] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleMenuTypeChange = (value: MenuType) => {
    onMenuTypeChange(value);
    setOpened(false);
  };

  return (
    <>
      <div
        className={classnames(
          "ml-md px-md py-sm text-sm rounded flex font-bold flex-initial",
          {
            "bg-green text-white": color === "green",
            "bg-leafly-white text-black": color === "white",
          }
        )}
        onClick={() => setOpened(true)}
        onKeyPress={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            setOpened(true);
          }
        }}
        ref={buttonRef}
        role="button"
        tabIndex={0}
        data-testid="menu-search-type"
      >
        <div>Menu:</div>

        <div
          className={classnames("ml-md flex", {
            "text-green": color === "white",
            "text-white": color === "green",
          })}
        >
          <div>{getShortTitle(currentType)}</div>

          <SolidArrowIcon />
        </div>
      </div>

      {opened && (
        <InformationalModal
          onDismissed={() => setOpened(false)}
          returnFocusRef={buttonRef}
          title="Choose your menu"
        >
          <RadioGroup name="menuType">
            {MODAL_OPTIONS.map(({ value, label }) => (
              <Radio
                key={value}
                id={label}
                checked={currentType === value}
                label={label}
                ariaLabel={label}
                className="mb-lg"
                onChange={() => {
                  handleMenuTypeChange(value);
                }}
                onClick={() => {
                  handleMenuTypeChange(value);
                }}
                data-testid={`menu-search-type-option-${value}`}
              />
            ))}
          </RadioGroup>

          <div>
            <p>
              *You need a valid medical ID to shop from a medical menu. Your ID
              number is required at checkout.
            </p>
            <p>
              <span className="font-bold">Good news:</span> You can choose to
              save your med ID to your Leafly profile for faster checkout.
            </p>
          </div>
        </InformationalModal>
      )}
    </>
  );
};
